import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  acknowledgeWorkspaceWarnings,
  getWorkspaceAuditEvents,
  getWorkspaceWarnings,
} from "../../api/workspaceCompliance";
import WorkspaceComplianceFrame from "../../components/workspaceCompliance/WorkspaceComplianceFrame";
import {
  fieldClass,
  InputLabel,
  SectionCard,
  WarningBanner,
} from "../../components/workspaceCompliance/WorkspaceComplianceUi";
import { useAuth } from "../../context/AuthContext";
import useOperatorWorkspace from "../../hooks/useOperatorWorkspace";
import { titleCaseComplianceLabel } from "../../constants/workspaceCompliance";
import { getAdvancedRecordTooltip } from "../../components/workspaceCompliance/advancedRecordsHelpContent";

function formatDateTime(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return date.toLocaleString();
}

function formatDateLabel(value) {
  if (!value) return "Date not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date not recorded";
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTimeLabel(value) {
  if (!value) return "Time not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time not recorded";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

const FIELD_LABELS = {
  agreement_kind: "Agreement type",
  agreement_template_id: "Agreement record",
  agreement_version: "Agreement version",
  allow_renter_guest_checkout: "Guest checkout",
  backup_provider_authorized: "Backup provider authorized",
  canonical_workspace_id: "Workspace record",
  client_ownership_mode: "Client ownership",
  default_client_ownership_mode: "Client ownership",
  default_occupancy_mode: "Rental relationship",
  end_date: "End date",
  follow_up_model: "Follow-up handling",
  host_visibility_scope: "Host visibility",
  marketplace_status: "Listing status",
  note: "Review note",
  occupancy_mode: "Rental relationship",
  ownership_snapshot_id: "Setup snapshot",
  payment_collector: "Payment collector",
  pricing_snapshot: "Pricing snapshot",
  profile_id: "Setup profile",
  publish_blocked: "Publishing",
  rental_id: "Rental record",
  rental_status: "Rental status",
  requires_agreement_acknowledgement: "Agreement acknowledgement",
  salon_join_request_id: "Salon request",
  start_date: "Start date",
  status: "Status",
  version: "Version",
  visibility: "Visibility",
  warning_state_level: "Setup check level",
  warning_version: "Setup check version",
  warranty_window_days: "Warranty window",
  workspace_code: "Workspace code",
  workspace_id: "Workspace",
};

const VALUE_LABELS = {
  active: "Active",
  approved: "Approved",
  booking: "Booking",
  cancelled: "Cancelled",
  deposit_due: "Deposit due",
  draft: "Draft",
  flat_rent: "Flat rent",
  hidden: "Hidden",
  high: "High priority",
  host_operated: "Host operated",
  host_supported_per_policy: "Host supported per policy",
  independent_owned: "Renter owns the client relationship",
  independent_owner: "Independent renter",
  lease: "Rental agreement",
  manual: "Manual review",
  marketplace: "Marketplace listing",
  none: "None",
  owner: "Owner",
  private: "Hidden",
  provider_only: "Renter handles follow-up",
  public: "Public",
  published: "Published",
  renter: "Renter",
  salon_owned: "Salon owns the client relationship",
  shared: "Shared",
  staff: "Staff",
  terminated: "Terminated",
  workspace: "Workspace",
};

const EVENT_COPY = {
  booking_approved: {
    title: "Rental request approved",
    description: "A workspace rental request was approved.",
  },
  booking_cancelled: {
    title: "Rental request cancelled",
    description: "A workspace rental request was cancelled.",
  },
  booking_compliance_snapshot_created: {
    title: "Booking setup record saved",
    description: "A setup record was saved for a booking.",
  },
  booking_created: {
    title: "Rental request created",
    description: "A renter submitted or created a workspace rental request.",
  },
  lease_activated: {
    title: "Rental agreement activated",
    description: "A rental agreement was activated for this workspace.",
  },
  lease_cancelled: {
    title: "Rental agreement cancelled",
    description: "A rental agreement was cancelled.",
  },
  lease_compliance_snapshot_created: {
    title: "Agreement setup record saved",
    description: "A setup record was saved for a rental agreement.",
  },
  lease_created: {
    title: "Rental agreement created",
    description: "A rental agreement was created for this workspace.",
  },
  lease_terminated: {
    title: "Rental agreement ended",
    description: "A rental agreement was ended.",
  },
  workspace_agreement_accepted: {
    title: "Agreement accepted",
    description: "A renter accepted the current workspace agreement.",
  },
  workspace_agreement_template_created: {
    title: "Agreement text saved",
    description: "Agreement text was saved for this workspace.",
  },
  workspace_compliance_profile_updated: {
    title: "Setup checks updated",
    description: "The renter setup check details were updated.",
  },
  workspace_created: {
    title: "Workspace created",
    description: "This workspace listing was created.",
  },
  workspace_deleted: {
    title: "Workspace deleted",
    description: "A draft workspace was deleted.",
  },
  workspace_marketplace_updated: {
    title: "Marketplace listing updated",
    description: "Marketplace listing details were changed.",
  },
  workspace_ownership_rules_updated: {
    title: "Client and payment control updated",
    description: "Client ownership, payment collection, or agreement acknowledgement settings were changed.",
  },
  workspace_rental_snapshot_created: {
    title: "Rental setup record saved",
    description: "A setup record was saved for this rental.",
  },
  workspace_superadmin_availability_updated: {
    title: "Availability updated",
    description: "Workspace availability was updated.",
  },
  workspace_updated: {
    title: "Workspace listing updated",
    description: "Workspace listing, visibility, or availability details were changed.",
  },
  workspace_warning_acknowledged: {
    title: "Setup checks reviewed",
    description: "The owner marked the current setup checks as reviewed.",
  },
  warning_generated: {
    title: "Setup check created",
    description: "The system found setup items for the owner to review.",
  },
};

function readableKey(value) {
  return FIELD_LABELS[value] || titleCaseComplianceLabel(value);
}

function readableValue(value) {
  if (value === null || value === undefined || value === "") return "Not recorded";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    return VALUE_LABELS[value] || titleCaseComplianceLabel(value);
  }
  return null;
}

function buildPayloadRows(payload) {
  if (!payload || typeof payload !== "object") return [];
  const rows = [];
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    if (key === "ownership_rules" && value && typeof value === "object") {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        rows.push({
          label: readableKey(nestedKey),
          value: readableValue(nestedValue),
        });
      });
      return;
    }
    if (key === "pricing_snapshot" || key === "risk_flags_json" || key === "snapshot_json") {
      return;
    }
    const displayValue = readableValue(value);
    if (displayValue) {
      rows.push({
        label: readableKey(key),
        value: displayValue,
      });
    }
  });
  return rows.slice(0, 8);
}

function getEventCopy(event = {}) {
  const type = String(event.event_type || "");
  const copy = EVENT_COPY[type] || {
    title: titleCaseComplianceLabel(type || "Activity recorded"),
    description: "An activity record was saved for this workspace.",
  };
  return copy;
}

function getEventSignature(event) {
  const rows = buildPayloadRows(event.payload_json);
  const rowSignature = rows
    .map((row) => `${row.label}:${row.value}`)
    .join("|");
  return [
    event.event_type || "",
    event.entity_type || "",
    event.compliance_warning_type || "",
    rowSignature,
  ].join("::");
}

function getDateKey(value) {
  if (!value) return "not-recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "not-recorded";
  return date.toISOString().slice(0, 10);
}

function dedupeVisibleEvents(eventRows) {
  const seenForDate = new Set();
  return eventRows.filter((event) => {
    const signature = `${getDateKey(event.created_at)}::${getEventSignature(event)}`;
    if (seenForDate.has(signature)) return false;
    seenForDate.add(signature);
    return true;
  });
}

function groupEventsByDate(eventRows) {
  return eventRows.reduce((groups, event) => {
    const dateKey = getDateKey(event.created_at);
    const existing = groups.find((group) => group.key === dateKey);
    if (existing) {
      existing.events.push(event);
      return groups;
    }
    groups.push({
      key: dateKey,
      label: formatDateLabel(event.created_at),
      events: [event],
    });
    return groups;
  }, []);
}

function AuditEventCard({ event }) {
  const [expanded, setExpanded] = useState(false);
  const copy = getEventCopy(event);
  const rows = buildPayloadRows(event.payload_json);
  const warningLabel = event.compliance_warning_type
    ? titleCaseComplianceLabel(event.compliance_warning_type)
    : "";

  return (
    <article className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        className="flex w-full min-w-0 flex-wrap items-start justify-between gap-3 text-left"
        aria-expanded={expanded}
      >
        <div className="min-w-0">
          <div className="break-words text-base font-semibold text-slate-900">
            {copy.title}
          </div>
          <p className="mt-1 break-words text-sm leading-6 text-slate-600">{copy.description}</p>
        </div>
        <span className="flex shrink-0 items-center gap-3 text-sm text-slate-500">
          {formatTimeLabel(event.created_at)}
          <span className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
            {expanded ? "Hide details" : "View details"}
          </span>
        </span>
      </button>
      {expanded ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
            {readableKey(event.entity_type)}
          </span>
          {warningLabel ? (
            <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
              Review item: {warningLabel}
            </span>
          ) : null}
        </div>
      ) : null}
      {expanded && rows.length ? (
        <div className="mt-4 grid gap-2">
          {rows.map((row) => (
            <div
              key={`${event.id}-${row.label}`}
              className="flex min-w-0 flex-col gap-1 rounded-2xl border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-start sm:justify-between"
            >
              <span className="break-words text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                {row.label}
              </span>
              <span className="break-words text-sm font-semibold text-slate-900 sm:text-right">
                {row.value}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {expanded && rows.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-600">
          No detailed setup values were recorded for this item.
        </div>
      ) : null}
    </article>
  );
}

function SetupHistoryGroup({ group }) {
  const [expanded, setExpanded] = useState(false);
  const count = group.events.length;

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        className="flex w-full min-w-0 flex-col gap-2 px-4 py-4 text-left sm:flex-row sm:items-center sm:justify-between"
        aria-expanded={expanded}
      >
        <span className="break-words text-base font-semibold text-slate-900">
          {group.label}
        </span>
        <span className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <span>
            {count} {count === 1 ? "setup change" : "setup changes"}
          </span>
          <span className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700">
            {expanded ? "Hide" : "Show"}
          </span>
        </span>
      </button>
      {expanded ? (
        <div className="space-y-3 border-t border-slate-200 bg-slate-50 p-3">
          {group.events.map((event) => (
            <AuditEventCard key={event.id} event={event} />
          ))}
        </div>
      ) : null}
    </article>
  );
}

export default function OperatorWorkspaceWarningsPage() {
  const { id } = useParams();
  const { token } = useAuth();
  const { workspace, error: workspaceError } = useOperatorWorkspace(id);
  const [warnings, setWarnings] = useState(null);
  const [events, setEvents] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const historyGroups = useMemo(
    () => groupEventsByDate(dedupeVisibleEvents(events)),
    [events]
  );

  const loadPage = useCallback(async () => {
    if (!token || !id) return;
    const [warningsResponse, eventRows] = await Promise.all([
      getWorkspaceWarnings(id, token),
      getWorkspaceAuditEvents(id, token),
    ]);
    setWarnings(warningsResponse || null);
    setEvents(Array.isArray(eventRows) ? eventRows : []);
  }, [id, token]);

  useEffect(() => {
    if (!token || !id) return undefined;
    let active = true;
    setLoading(true);
    setError("");

    Promise.resolve()
      .then(loadPage)
      .catch((err) => {
        if (active) {
          setError(err.message || "Unable to load workspace warnings.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [id, loadPage, token]);

  const handleAcknowledge = async (event) => {
    event.preventDefault();
    if (!token || !id) return;
    setSaving(true);
    setError("");
    setFlash("");
    try {
      const response = await acknowledgeWorkspaceWarnings(
        id,
        { note: note || null },
        token
      );
      setWarnings(response || null);
      setNote("");
      await loadPage();
      setFlash("Workspace warnings acknowledged.");
    } catch (err) {
      setError(err.message || "Unable to acknowledge workspace warnings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceComplianceFrame
      workspace={workspace}
      workspaceId={id}
      title="Setup History"
      description="Review setup checks, owner acknowledgement, and dated setup changes for this workspace."
    >
      {workspaceError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {workspaceError}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {flash ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {flash}
        </div>
      ) : null}

      <WarningBanner
        strictModeEnabled={Boolean(warnings?.strict_mode_enabled)}
        publishBlocked={Boolean(warnings?.publish_blocked)}
        agreementCompletionBlocked={Boolean(warnings?.agreement_completion_blocked)}
        warningStateLevel={warnings?.warning_state_level || "none"}
        warnings={warnings?.warnings || []}
        disclaimer
      />

      <div className="grid gap-6 xl:grid-cols-[0.88fr,1.12fr]">
        <SectionCard
          title="Owner Acknowledgement"
          description="If a setup item needs review, the salon owner can mark the current version reviewed after checking it."
          tooltip="Use this to record that the owner reviewed the current setup check version."
        >
          {loading ? <p className="text-sm text-slate-600">Loading setup checks...</p> : null}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Current check version
              </div>
              <div className="mt-2 text-base font-semibold text-slate-900">
                {warnings?.warning_version || "Not generated"}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Owner review
              </div>
              <div className="mt-2 text-base font-semibold text-slate-900">
                {warnings?.operator_acknowledgement?.current ? "Current version reviewed" : "Not reviewed"}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Reviewed by
              </div>
              <div className="mt-2 text-base font-semibold text-slate-900">
                {warnings?.operator_acknowledgement?.acknowledged_by_user_id || "Not recorded"}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Reviewed at
              </div>
              <div className="mt-2 text-base font-semibold text-slate-900">
                {formatDateTime(warnings?.operator_acknowledgement?.acknowledged_at)}
              </div>
            </div>
          </div>

          <form className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5" onSubmit={handleAcknowledge}>
            <InputLabel label="Review note" tooltip={getAdvancedRecordTooltip("Review note")}>
              <input
                className={fieldClass}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Example: reviewed rental setup before publishing."
              />
            </InputLabel>
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving..." : "Mark Setup Checks Reviewed"}
            </button>
          </form>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
            These checks are here to help you catch setup conflicts before a renter sees the listing or agreement. They are not legal advice, but they give you a clear place to review and record owner decisions.
          </div>
        </SectionCard>

        <SectionCard
          title="Setup History"
          description="Click a date to see the setup changes saved on that day. Open a change to see the details."
          tooltip={getAdvancedRecordTooltip("Setup History")}
        >
          <div className="space-y-3">
            {historyGroups.map((group) => (
              <SetupHistoryGroup key={group.key} group={group} />
            ))}
            {historyGroups.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-600">
                No setup history has been recorded for this workspace yet.
              </div>
            ) : null}
          </div>
        </SectionCard>
      </div>
    </WorkspaceComplianceFrame>
  );
}
